import { getAuthTokenFromCookie } from "./cookies";

/**
 * Image URL utilities for handling Azure blob storage URLs.
 *
 * Legacy images were uploaded with direct Azure blob URLs that are being
 * deprecated in favor of authenticated proxy endpoints. This module provides
 * utilities to transform legacy URLs to proxy URLs at render time.
 */

const AZURE_BLOB_PATTERN =
  /^https:\/\/byobpdfstorage\.blob\.core\.windows\.net\/animages\/(.+)$/;

/** Internal K8s service URL for PDF generator */
const CLUSTER_API_BASE = "http://ani-api.ani-api:8001/api/v1";

/**
 * Detects if we're in K8s PDF export mode (running in PDF generator's browser).
 * Only returns true for /pdf route with export=pdf or export=png query param.
 * 
 * Note: host.docker.internal uses Vite proxy, not K8s internal URL.
 */
function isK8sPdfExportMode(): boolean {
  if (typeof window === "undefined") return false;

  // K8s PDF generator uses /pdf route with export param
  if (window.location.pathname === "/pdf") {
    const searchParams = new URLSearchParams(window.location.search);
    const exportParam = searchParams.get("export");
    if (exportParam === "pdf" || exportParam === "png") {
      return true;
    }
  }

  return false;
}

/**
 * Gets the auth token from URL params (PDF export) or localStorage (browser).
 * In PDF export mode, the token is passed via URL ?token= parameter.
 * Returns undefined if not available (SSR, not logged in, etc.)
 */
function getAuthToken(): string | undefined {
  if (typeof window === "undefined") return undefined;

  // First, check URL params (PDF generator passes token this way)
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get("token");
    if (urlToken) {
      return urlToken;
    }
  } catch {
    // Ignore URL parse errors
  }

  // Fall back to localStorage (normal browser session)
  try {
    const authData = localStorage.getItem("auth_data");
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed.auth_token || undefined;
    }
  } catch {
    // Ignore parse errors
  }

  // Readable session cookie (when present — HttpOnly cookies are not visible here)
  try {
    const cookieToken = getAuthTokenFromCookie(document.cookie, "auth_token");
    if (cookieToken) {
      return cookieToken;
    }
  } catch {
    // Ignore
  }

  return undefined;
}

/**
 * Appends `?token=` (or `&token=`) when an auth token is available.
 * Skips if the URL already has a token param. Used for `<img>` requests that
 * cannot send Authorization headers (PDF /pdf page and legacy blob proxy).
 */
function appendAuthTokenIfPresent(url: string): string {
  const token = getAuthToken();
  if (!token || /[?&]token=/.test(url)) {
    return url;
  }
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}token=${encodeURIComponent(token)}`;
}

/**
 * True for authenticated image proxy routes (`/api/v1/images/{id}` or legacy).
 */
export function isApiImageProxyUrl(url: string): boolean {
  if (url.startsWith("data:") || url.startsWith("/images/")) {
    return false;
  }
  try {
    const pathname = url.startsWith("http")
      ? new URL(url).pathname
      : url.split("?")[0];
    return /\/api\/v1\/images\//.test(pathname);
  } catch {
    return /\/api\/v1\/images\//.test(url);
  }
}

/**
 * True when image requests should use the internal K8s API host.
 * Matches app/services/api/client.ts:getApiBaseUrl() — host.docker.internal
 * keeps relative /api/v1 (Vite proxy) even when export=pdf.
 */
function shouldUseClusterImageApiBase(): boolean {
  if (typeof window === "undefined") return false;
  if (window.location.hostname === "host.docker.internal") {
    return false;
  }
  return isK8sPdfExportMode();
}

/**
 * Rewrites `/api/v1/images/...` to the cluster API host in AKS PDF export.
 * ani-app.ani-app serves static files only; relative /api/v1 would 404 in export mode.
 */
function resolveApiImageProxyUrl(url: string): string {
  if (!shouldUseClusterImageApiBase()) {
    return url;
  }

  let pathAndQuery: string;
  if (url.startsWith("http")) {
    try {
      const parsed = new URL(url);
      pathAndQuery = parsed.pathname + parsed.search;
    } catch {
      const proxyPathMatch = url.match(/\/api\/v1\/images\/[^?\s#]+(?:\?[^#\s]*)?/);
      pathAndQuery =
        proxyPathMatch?.[0] ?? (url.startsWith("/") ? url : `/${url}`);
    }
  } else {
    pathAndQuery = url.startsWith("/") ? url : `/${url}`;
  }

  const clusterOrigin = new URL(CLUSTER_API_BASE).origin;
  return `${clusterOrigin}${pathAndQuery}`;
}

/**
 * Gets the appropriate API base URL for image proxy.
 * Uses internal K8s URL when in AKS PDF export mode, relative URL otherwise.
 */
function getImageApiBase(): string {
  if (shouldUseClusterImageApiBase()) {
    return CLUSTER_API_BASE;
  }
  return "/api/v1";
}

/**
 * Transforms Azure blob storage URLs to authenticated proxy URLs.
 * Passes through non-Azure URLs unchanged.
 *
 * In K8s PDF export mode (/pdf?export=pdf), uses internal K8s service URL.
 * In browser mode and Docker PDF mode, uses relative URL (proxied by frontend).
 *
 * @param url - The image URL (may be Azure blob URL or proxy URL)
 * @returns Proxy URL for Azure URLs, original URL otherwise
 *
 * @example
 * // Azure URL gets transformed (with auth token appended)
 * toProxyUrl("https://byobpdfstorage.blob.core.windows.net/animages/production/company/abc123")
 * // Returns: "/api/v1/images/legacy?path=production%2Fcompany%2Fabc123&token=xxx"
 *
 * @example
 * // In PDF export mode, uses internal K8s URL
 * // Returns: "http://ani-api.ani-api:8001/api/v1/images/legacy?path=...&token=..."
 *
 * @example
 * // Proxy URLs get auth token when available (required for <img> on /pdf)
 * toProxyUrl("/api/v1/images/abc-123")
 * // Page View: "/api/v1/images/abc-123?token=xxx"
 * // Generate Report (AKS): "http://ani-api.ani-api:8001/api/v1/images/abc-123?token=xxx"
 *
 * @example
 * // Local URLs pass through unchanged
 * toProxyUrl("/images/logo.png")
 * // Returns: "/images/logo.png"
 */
export function toProxyUrl(url: string | undefined | null): string | undefined {
  if (!url) return undefined;

  const match = url.match(AZURE_BLOB_PATTERN);
  if (match) {
    const blobPath = match[1];
    const apiBase = getImageApiBase();

    // Prefer direct proxy by image id when the blob path ends with a UUID.
    // Works for locally stored uploads and avoids the Azure-backed legacy route.
    const lastSegment = blobPath.split("/").pop() || "";
    const fileId = lastSegment.replace(/\.[^.]+$/, "");
    if (/^[a-f0-9-]{36}$/i.test(fileId)) {
      return appendAuthTokenIfPresent(`${apiBase}/images/${fileId}`);
    }

    const encodedPath = encodeURIComponent(blobPath);
    return appendAuthTokenIfPresent(
      `${apiBase}/images/legacy?path=${encodedPath}`
    );
  }

  if (isApiImageProxyUrl(url)) {
    return appendAuthTokenIfPresent(resolveApiImageProxyUrl(url));
  }

  return url;
}

/**
 * True when an API image URL still needs session-authenticated fetch (SSO cookie)
 * because no `?token=` is available for a plain `<img>` tag.
 */
export function apiImageUrlNeedsSessionFetch(url: string): boolean {
  if (!isApiImageProxyUrl(url)) {
    return false;
  }
  return !/[?&]token=/.test(url);
}

/**
 * Rewrites `<img src="...">` URLs in TipTap HTML through {@link toProxyUrl}.
 */
export function proxyImageUrlsInHtml(html: string): string {
  if (!html) return html;

  return html.replace(
    /(<img\b[^>]*\bsrc=)(["'])([^"']+)\2/gi,
    (match, prefix: string, quote: string, src: string) => {
      const proxied = toProxyUrl(src);
      if (!proxied || proxied === src) {
        return match;
      }
      return `${prefix}${quote}${proxied}${quote}`;
    }
  );
}

/**
 * Checks if a URL is a direct Azure blob storage URL.
 *
 * @param url - The URL to check
 * @returns true if the URL points to Azure blob storage
 */
export function isAzureBlobUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  return AZURE_BLOB_PATTERN.test(url);
}
